import { useCallback, useEffect, useState } from 'react';
import {
  incidentArtifactService,
  ARTIFACT_ORDER,
} from '../services/incidentArtifactService';

// One stable empty array, so a consumer's effect keyed on `links` does not re-run every render.
const NONE = [];

// Known kinds only, in display order, with a usable link. A kind this client has no name for
// (one added server-side later) is dropped rather than shown unlabelled.
const toLinks = (list) =>
  ARTIFACT_ORDER.map((kind) => {
    const found = (list || []).find((a) => a.kind === kind && a.externalUrl);
    return found ? { kind, url: found.externalUrl } : null;
  }).filter(Boolean);

/**
 * useIncidentArtifacts - the Exec Summary Slide and Document links of one incident
 * (US2174775). The incident page owns this single fetch and shares it with the Executive
 * Summary row and, for Eternium, the editor.
 *
 * `links` is an ordered array of { kind, url }. It is empty, immediately, whenever `problemId`
 * or `enabled` changes: the page route has no key, so the page is reused across incidents, and
 * without that incident A's icons would show on incident B and stay there if B's fetch is
 * disabled or fails. A response that arrives after the id has changed is ignored.
 *
 * A failure never throws: `links` stays empty and `error` carries a user-facing message.
 *
 * @param {string} problemId
 * @param {boolean} enabled - false until the incident is known to be a past, non-archived one
 * @returns {{links: {kind: string, url: string}[], loading: boolean, error: string,
 *            reload: () => void, setLink: (kind: string, url: string) => void}}
 */
export default function useIncidentArtifacts(problemId, enabled) {
  const [state, setState] = useState({ id: null, links: NONE, error: '', loading: false });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !problemId) return undefined;
    let cancelled = false;

    setState((prev) =>
      prev.id === problemId
        ? { ...prev, error: '', loading: true }
        : { id: problemId, links: NONE, error: '', loading: true }
    );
    incidentArtifactService
      .getArtifacts(problemId)
      .then((list) => {
        if (!cancelled) setState({ id: problemId, links: toLinks(list), error: '', loading: false });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ id: problemId, links: NONE, error: err?.message || 'Something went wrong. Please try again.', loading: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [problemId, enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Applies a Save or Clear the editor already made, so the row follows it with no refetch.
  const setLink = useCallback(
    (kind, url) => {
      setState((prev) => {
        if (prev.id !== problemId) return prev;
        const without = prev.links.filter((l) => l.kind !== kind);
        const next = url ? [...without, { kind, url }] : without;
        return {
          ...prev,
          links: ARTIFACT_ORDER.map((k) => next.find((l) => l.kind === k)).filter(Boolean),
        };
      });
    },
    [problemId]
  );

  const current = enabled && !!problemId && state.id === problemId;
  return {
    links: current ? state.links : NONE,
    // True from the first render of an enabled incident until its own response lands, so a
    // consumer never shows a "no links" state for a fetch that has not started.
    loading: enabled && !!problemId && (!current || state.loading),
    error: current ? state.error : '',
    reload,
    setLink,
  };
}
